import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckIcon from "@mui/icons-material/Check";
import DevicesIcon from "@mui/icons-material/Devices";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InternNotificationsCardCreateHubMutation } from "./__generated__/InternNotificationsCardCreateHubMutation.graphql";
import { InternNotificationsCardSendTestMutation } from "./__generated__/InternNotificationsCardSendTestMutation.graphql";
import { InternNotificationsCardSyncMutation } from "./__generated__/InternNotificationsCardSyncMutation.graphql";
import { InternNotificationsCardUpdateAndroidMutation } from "./__generated__/InternNotificationsCardUpdateAndroidMutation.graphql";
import { InternNotificationsCardUpdateIosMutation } from "./__generated__/InternNotificationsCardUpdateIosMutation.graphql";
import { InternNotificationsCard_brand$key } from "./__generated__/InternNotificationsCard_brand.graphql";
import { useColorMode } from "../../../contexts/ColorModeContext";
import SnackbarContext from "../../../contexts/SnackbarContext";
import formatNumber from "../../../utils/formatNumber";
import { InternPanel } from "../components";

const brandFragment = graphql`
  fragment InternNotificationsCard_brand on BrandType {
    id
    displayName
    iosPushNotificationCredentialsSetInAzure
    iosPushNotificationCredentialsRequireServiceKeyMigration
    iosPushNotificationCertExpirationDate
    androidPushNotificationCredentialsSetInAzure
    androidPushNotificationRequiresFcmv1Migration
    pushNotificationStats {
      notificationsSent30d
      pushDeliveries30d
      pushTaps30d
      tapRate
      totalRegisteredDevices
      iosDevices
      androidDevices
      dailyMetrics {
        date
        deliveries
        taps
      }
    }
    appConfig {
      id
      azureNotificationsHubName
      iosAscTeamId
      iosAppIdentifier
      iosPushNotificationServiceKey
      iosPushNotificationServiceKeyId
      androidPushNotificationFcmv1PrivateKey
      androidPushNotificationFcmv1ProjectId
      androidPushNotificationFcmv1ClientEmail
    }
  }
`;

const createHubMutation = graphql`
  mutation InternNotificationsCardCreateHubMutation(
    $brandId: ID!
    $hubName: String
  ) {
    createAzureNotificationHub(brandId: $brandId, hubName: $hubName) {
      ... on BrandAppConfigType {
        id
        azureNotificationsHubName
      }
    }
  }
`;

const syncMutation = graphql`
  mutation InternNotificationsCardSyncMutation($brandId: ID!) {
    syncPushNotificationCredentials(brandId: $brandId) {
      ...InternNotificationsCard_brand
    }
  }
`;

const updateIosMutation = graphql`
  mutation InternNotificationsCardUpdateIosMutation(
    $input: UpdateIosPushNotificationCredentialsInput!
  ) {
    updateIosPushNotificationCredentials(input: $input) {
      ...InternNotificationsCard_brand
    }
  }
`;

const updateAndroidMutation = graphql`
  mutation InternNotificationsCardUpdateAndroidMutation(
    $input: UpdateAndroidPushNotificationCredentialsInput!
  ) {
    updateAndroidPushNotificationCredentials(input: $input) {
      ...InternNotificationsCard_brand
    }
  }
`;

const sendTestMutation = graphql`
  mutation InternNotificationsCardSendTestMutation(
    $brandId: ID!
    $deviceToken: String!
    $platform: String!
    $title: String!
    $body: String!
  ) {
    sendTestPushNotification(
      brandId: $brandId
      deviceToken: $deviceToken
      platform: $platform
      title: $title
      body: $body
    ) {
      success
      error
    }
  }
`;

type Props = {
  brand: InternNotificationsCard_brand$key;
};

interface StepStatus {
  completed: boolean;
  hasIssues: boolean;
  label: string;
  details?: string;
  outOfSync?: boolean;
}

const InternNotificationsCard = ({ brand: brandKey }: Props) => {
  const brand = useFragment(brandFragment, brandKey);
  const { colorMode } = useColorMode();

  const chartColors = useMemo(
    () => ({
      grid: colorMode === "dark" ? "#374151" : "#e5e7eb",
      text: colorMode === "dark" ? "#9ca3af" : "#6b7280",
      tooltip: {
        bg: colorMode === "dark" ? "#1f2937" : "#ffffff",
        border: colorMode === "dark" ? "#374151" : "#e5e7eb",
      },
    }),
    [colorMode]
  );

  const chartData = useMemo(
    () =>
      brand.pushNotificationStats.dailyMetrics.map((m) => ({
        date: new Date(m.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        Deliveries: m.deliveries,
        Taps: m.taps,
      })),
    [brand.pushNotificationStats.dailyMetrics]
  );

  const [customHubName, setCustomHubName] = useState("");
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [androidModalOpen, setAndroidModalOpen] = useState(false);
  const [testPushModalOpen, setTestPushModalOpen] = useState(false);
  const [testPushFormData, setTestPushFormData] = useState({
    deviceToken: "",
    platform: "ios" as "ios" | "android",
    title: "Test Notification",
    body: "",
  });

  const decodePrivateKey = (
    base64Key: string,
    keyType: string = "key"
  ): string => {
    if (!base64Key) return "";
    try {
      return atob(base64Key);
    } catch {
      return base64Key;
    }
  };

  const [iosFormData, setIosFormData] = useState({
    serviceKey: decodePrivateKey(
      brand.appConfig.iosPushNotificationServiceKey || "",
      "iOS service key"
    ),
    serviceKeyId: brand.appConfig.iosPushNotificationServiceKeyId || "",
  });

  const [androidFormData, setAndroidFormData] = useState({
    privateKey: decodePrivateKey(
      brand.appConfig.androidPushNotificationFcmv1PrivateKey || "",
      "Android private key"
    ),
    projectId: brand.appConfig.androidPushNotificationFcmv1ProjectId || "",
    clientEmail: brand.appConfig.androidPushNotificationFcmv1ClientEmail || "",
  });

  useEffect(() => {
    setIosFormData({
      serviceKey: decodePrivateKey(
        brand.appConfig.iosPushNotificationServiceKey || ""
      ),
      serviceKeyId: brand.appConfig.iosPushNotificationServiceKeyId || "",
    });
    setAndroidFormData({
      privateKey: decodePrivateKey(
        brand.appConfig.androidPushNotificationFcmv1PrivateKey || ""
      ),
      projectId: brand.appConfig.androidPushNotificationFcmv1ProjectId || "",
      clientEmail:
        brand.appConfig.androidPushNotificationFcmv1ClientEmail || "",
    });
  }, [
    brand.appConfig.iosPushNotificationServiceKey,
    brand.appConfig.iosPushNotificationServiceKeyId,
    brand.appConfig.androidPushNotificationFcmv1PrivateKey,
    brand.appConfig.androidPushNotificationFcmv1ProjectId,
    brand.appConfig.androidPushNotificationFcmv1ClientEmail,
  ]);

  const snackbarContext = useContext(SnackbarContext);

  const [createHub, isCreating] =
    useMutation<InternNotificationsCardCreateHubMutation>(createHubMutation);
  const [sync, isSyncInFlight] =
    useMutation<InternNotificationsCardSyncMutation>(syncMutation);
  const [updateIos, isUpdatingIos] =
    useMutation<InternNotificationsCardUpdateIosMutation>(updateIosMutation);
  const [updateAndroid, isUpdatingAndroid] =
    useMutation<InternNotificationsCardUpdateAndroidMutation>(
      updateAndroidMutation
    );
  const [sendTest, isSendingTest] =
    useMutation<InternNotificationsCardSendTestMutation>(sendTestMutation);

  const handleSync = useCallback(() => {
    sync({
      variables: { brandId: brand.id },
      onCompleted: () => {
        snackbarContext?.openSnackbar(
          "Credentials synced successfully",
          "success"
        );
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(`Sync failed: ${error.message}`, "error");
      },
    });
  }, [sync, brand.id, snackbarContext]);

  const handleCreateHub = useCallback(() => {
    createHub({
      variables: { brandId: brand.id, hubName: customHubName.trim() || null },
      onCompleted: (_, errors) => {
        if (errors) {
          snackbarContext?.openSnackbar("Failed to create hub", "error");
        } else {
          setCustomHubName("");
          snackbarContext?.openSnackbar("Hub created successfully", "success");
        }
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(`Failed: ${error.message}`, "error");
      },
    });
  }, [createHub, brand.id, customHubName, snackbarContext]);

  const handleIosFormChange =
    (field: keyof typeof iosFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIosFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleAndroidFormChange =
    (field: keyof typeof androidFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAndroidFormData((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleIosSave = useCallback(() => {
    updateIos({
      variables: {
        input: {
          brandId: brand.id,
          serviceKey: iosFormData.serviceKey,
          serviceKeyId: iosFormData.serviceKeyId,
        },
      },
      onCompleted: () => {
        snackbarContext?.openSnackbar("iOS credentials updated", "success");
        setIosModalOpen(false);
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(`Failed: ${error.message}`, "error");
      },
    });
  }, [updateIos, brand.id, iosFormData, snackbarContext]);

  const handleAndroidSave = useCallback(() => {
    updateAndroid({
      variables: {
        input: {
          brandId: brand.id,
          privateKey: androidFormData.privateKey,
          projectId: androidFormData.projectId,
          clientEmail: androidFormData.clientEmail,
        },
      },
      onCompleted: () => {
        snackbarContext?.openSnackbar("Android credentials updated", "success");
        setAndroidModalOpen(false);
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(`Failed: ${error.message}`, "error");
      },
    });
  }, [updateAndroid, brand.id, androidFormData, snackbarContext]);

  const handleSendTestPush = useCallback(() => {
    sendTest({
      variables: {
        brandId: brand.id,
        deviceToken: testPushFormData.deviceToken,
        platform: testPushFormData.platform,
        title: testPushFormData.title,
        body:
          testPushFormData.body ||
          `This is a test push notification from ${brand.displayName}`,
      },
      onCompleted: (response) => {
        if (response.sendTestPushNotification.success) {
          snackbarContext?.openSnackbar(
            "Test push notification sent successfully",
            "success"
          );
          setTestPushModalOpen(false);
        } else {
          snackbarContext?.openSnackbar(
            `Failed: ${
              response.sendTestPushNotification.error || "Unknown error"
            }`,
            "error"
          );
        }
      },
      onError: (error) => {
        snackbarContext?.openSnackbar(`Failed: ${error.message}`, "error");
      },
    });
  }, [
    sendTest,
    brand.id,
    brand.displayName,
    testPushFormData,
    snackbarContext,
  ]);

  const getStepStatuses = (): StepStatus[] => {
    const steps: StepStatus[] = [];
    const hasAzureHub = !!brand.appConfig.azureNotificationsHubName;

    steps.push({
      completed: hasAzureHub,
      hasIssues: false,
      label: "Hub",
      details: hasAzureHub
        ? brand.appConfig.azureNotificationsHubName!
        : "Not configured",
    });

    const iosConfigured =
      brand.iosPushNotificationCredentialsSetInAzure &&
      brand.appConfig.iosAscTeamId &&
      brand.appConfig.iosAppIdentifier;
    let iosIssues = false;
    let iosDetails = "Configured";
    let iosOutOfSync = false;

    if (!brand.iosPushNotificationCredentialsSetInAzure) {
      iosDetails = "No credentials";
      iosIssues = true;
    } else if (brand.iosPushNotificationCredentialsRequireServiceKeyMigration) {
      iosDetails = brand.iosPushNotificationCertExpirationDate
        ? `Needs migration · Expires ${new Date(
            brand.iosPushNotificationCertExpirationDate
          ).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}`
        : "Needs migration";
      iosIssues = true;
    } else if (
      brand.iosPushNotificationCredentialsSetInAzure &&
      (!brand.appConfig.iosPushNotificationServiceKey ||
        !brand.appConfig.iosPushNotificationServiceKeyId)
    ) {
      iosDetails = "Out of sync";
      iosIssues = true;
      iosOutOfSync = true;
    }

    steps.push({
      completed: !!iosConfigured,
      hasIssues: iosIssues,
      label: "iOS",
      details: iosDetails,
      outOfSync: iosOutOfSync,
    });

    const androidConfigured =
      brand.androidPushNotificationCredentialsSetInAzure &&
      !brand.androidPushNotificationRequiresFcmv1Migration &&
      brand.appConfig.androidPushNotificationFcmv1PrivateKey &&
      brand.appConfig.androidPushNotificationFcmv1ProjectId &&
      brand.appConfig.androidPushNotificationFcmv1ClientEmail;
    let androidIssues = false;
    let androidDetails = "Configured";
    let androidOutOfSync = false;

    if (!brand.androidPushNotificationCredentialsSetInAzure) {
      androidDetails = "No credentials";
      androidIssues = true;
    } else if (brand.androidPushNotificationRequiresFcmv1Migration) {
      androidDetails = "Needs FCMv1";
      androidIssues = true;
    } else if (
      brand.androidPushNotificationCredentialsSetInAzure &&
      (!brand.appConfig.androidPushNotificationFcmv1PrivateKey ||
        !brand.appConfig.androidPushNotificationFcmv1ProjectId ||
        !brand.appConfig.androidPushNotificationFcmv1ClientEmail)
    ) {
      androidDetails = "Out of sync";
      androidIssues = true;
      androidOutOfSync = true;
    }

    steps.push({
      completed: !!androidConfigured,
      hasIssues: androidIssues,
      label: "Android",
      details: androidDetails,
      outOfSync: androidOutOfSync,
    });

    return steps;
  };

  const stepStatuses = getStepStatuses();
  const completedSteps = stepStatuses.filter((step) => step.completed).length;
  const progressPercentage = (completedSteps / stepStatuses.length) * 100;

  return (
    <>
      <InternPanel
        title="Push Notifications"
        subtitle="Configure push notification credentials"
        icon={<NotificationsIcon />}
        collapsible={false}
        actions={
          <Stack direction="row" spacing={1}>
            {stepStatuses[0].completed && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<SendIcon />}
                onClick={() => setTestPushModalOpen(true)}
              >
                Test
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutorenewIcon />}
              onClick={handleSync}
              disabled={isSyncInFlight}
            >
              Sync
            </Button>
            {!stepStatuses[0].completed && (
              <Button
                variant="contained"
                size="small"
                onClick={handleCreateHub}
                disabled={isCreating}
              >
                {isCreating ? "Creating..." : "Create Hub"}
              </Button>
            )}
          </Stack>
        }
      >
        <Stack spacing={2}>
          {/* Progress */}
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={0.5}
            >
              <Typography
                variant="caption"
                sx={{ color: "var(--intern-text-tertiary)" }}
              >
                Setup Progress
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "var(--intern-text-secondary)" }}
              >
                {completedSteps}/{stepStatuses.length}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: "var(--intern-bg-tertiary)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  bgcolor: stepStatuses.some((step) => step.hasIssues)
                    ? "var(--intern-status-warning)"
                    : completedSteps === stepStatuses.length
                    ? "var(--intern-status-success)"
                    : "var(--intern-accent)",
                },
              }}
            />
          </Box>

          {/* Steps Grid */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            {stepStatuses.map((step, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: 1,
                  bgcolor: "var(--intern-bg-secondary)",
                  border: "1px solid var(--intern-border-default)",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Box
                    sx={{
                      width: { xs: 18, sm: 20 },
                      height: { xs: 18, sm: 20 },
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      bgcolor: step.hasIssues
                        ? "var(--intern-status-warning)"
                        : step.completed
                        ? "var(--intern-status-success)"
                        : "var(--intern-bg-tertiary)",
                      color:
                        step.completed || step.hasIssues
                          ? "#fff"
                          : "var(--intern-text-tertiary)",
                      fontSize: "0.625rem",
                      fontWeight: 600,
                    }}
                  >
                    {step.completed ? (
                      <CheckIcon sx={{ fontSize: 12 }} />
                    ) : (
                      index + 1
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      color: step.hasIssues
                        ? "var(--intern-status-warning)"
                        : step.completed
                        ? "var(--intern-status-success)"
                        : "var(--intern-text-primary)",
                    }}
                  >
                    {step.label}
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--intern-text-tertiary)",
                    fontSize: "0.6875rem",
                    display: "block",
                  }}
                >
                  {step.details}
                </Typography>
                {/* Setup buttons */}
                {index === 1 && stepStatuses[0].completed && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setIosModalOpen(true)}
                    disabled={step.outOfSync}
                    sx={{ mt: 0.5, fontSize: "0.6875rem", p: 0.5, minWidth: 0 }}
                  >
                    Setup
                  </Button>
                )}
                {index === 2 && stepStatuses[0].completed && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setAndroidModalOpen(true)}
                    disabled={step.outOfSync}
                    sx={{ mt: 0.5, fontSize: "0.6875rem", p: 0.5, minWidth: 0 }}
                  >
                    Setup
                  </Button>
                )}
              </Box>
            ))}
          </Stack>

          {/* Create Hub Section */}
          {!stepStatuses[0].completed && (
            <Box>
              <Alert
                severity="info"
                sx={{ mb: 1.5, py: 0.5, fontSize: "0.75rem" }}
              >
                Create a notification hub to enable push notifications.
              </Alert>
              <TextField
                label="Hub Name (Optional)"
                placeholder="Auto-generated if empty"
                value={customHubName}
                onChange={(e) => setCustomHubName(e.target.value)}
                size="small"
                disabled={isCreating}
                fullWidth
              />
            </Box>
          )}

          {/* Delivery Stats */}
          {stepStatuses[0].completed && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--intern-text-tertiary)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.625rem",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Last 30 Days
                </Typography>
                <Stack
                  direction="row"
                  spacing={0}
                  sx={{
                    bgcolor: "var(--intern-bg-secondary)",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  {/* Sent */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      textAlign: "center",
                      borderRight: "1px solid var(--intern-border-default)",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={0.5}
                      mb={0.5}
                    >
                      <SendIcon
                        sx={{
                          fontSize: 14,
                          color: "var(--intern-text-tertiary)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "var(--intern-text-tertiary)",
                          fontSize: "0.625rem",
                        }}
                      >
                        Campaigns Sent
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "var(--intern-text-primary)",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      {formatNumber(
                        brand.pushNotificationStats.notificationsSent30d
                      )}
                    </Typography>
                  </Box>

                  {/* Delivered */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      textAlign: "center",
                      borderRight: "1px solid var(--intern-border-default)",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={0.5}
                      mb={0.5}
                    >
                      <DevicesIcon
                        sx={{
                          fontSize: 14,
                          color: "var(--intern-text-tertiary)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "var(--intern-text-tertiary)",
                          fontSize: "0.625rem",
                        }}
                      >
                        Delivered
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "var(--intern-text-primary)",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      {formatNumber(
                        brand.pushNotificationStats.pushDeliveries30d
                      )}
                    </Typography>
                  </Box>

                  {/* Taps */}
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      textAlign: "center",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={0.5}
                      mb={0.5}
                    >
                      <TouchAppIcon
                        sx={{
                          fontSize: 14,
                          color: "var(--intern-text-tertiary)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "var(--intern-text-tertiary)",
                          fontSize: "0.625rem",
                        }}
                      >
                        Taps
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "var(--intern-text-primary)",
                        fontWeight: 700,
                        fontSize: "1rem",
                      }}
                    >
                      {formatNumber(brand.pushNotificationStats.pushTaps30d)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#3b82f6", fontSize: "0.625rem" }}
                    >
                      {(brand.pushNotificationStats.tapRate * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Device Stats */}
              <Box>
                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    variant="caption"
                    sx={{
                      color: "var(--intern-text-tertiary)",
                      fontSize: "0.625rem",
                    }}
                  >
                    Registered Devices
                  </Typography>
                  <Stack direction="row" spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AppleIcon
                        sx={{
                          fontSize: 12,
                          color: "var(--intern-text-tertiary)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "var(--intern-text-secondary)",
                          fontSize: "0.625rem",
                        }}
                      >
                        {formatNumber(brand.pushNotificationStats.iosDevices)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <AndroidIcon
                        sx={{
                          fontSize: 12,
                          color: "var(--intern-text-tertiary)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "var(--intern-text-secondary)",
                          fontSize: "0.625rem",
                        }}
                      >
                        {formatNumber(
                          brand.pushNotificationStats.androidDevices
                        )}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--intern-text-tertiary)",
                        fontSize: "0.625rem",
                      }}
                    >
                      (
                      {formatNumber(
                        brand.pushNotificationStats.totalRegisteredDevices
                      )}{" "}
                      total)
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Deliveries & Taps Chart */}
              <Divider />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--intern-text-tertiary)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    fontSize: "0.625rem",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Daily Deliveries &amp; Taps (14 Days)
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient
                          id="brandDeliveries"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#6366f1"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor="#6366f1"
                            stopOpacity={0}
                          />
                        </linearGradient>
                        <linearGradient
                          id="brandTaps"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#10b981"
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor="#10b981"
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={chartColors.grid}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10, fill: chartColors.text }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: chartColors.text }}
                        tickLine={false}
                        axisLine={false}
                        width={35}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: chartColors.tooltip.bg,
                          border: `1px solid ${chartColors.tooltip.border}`,
                          borderRadius: 8,
                          fontSize: "0.75rem",
                        }}
                      />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: "0.6875rem" }}
                      />
                      <Area
                        type="monotone"
                        dataKey="Deliveries"
                        stroke="#6366f1"
                        fill="url(#brandDeliveries)"
                        strokeWidth={2}
                        isAnimationActive={false}
                      />
                      <Area
                        type="monotone"
                        dataKey="Taps"
                        stroke="#10b981"
                        fill="url(#brandTaps)"
                        strokeWidth={2}
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </>
          )}
        </Stack>
      </InternPanel>

      {/* iOS Setup Modal */}
      <Dialog
        open={iosModalOpen}
        onClose={() => setIosModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>iOS Push Notification Setup</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ py: 0.5, fontSize: "0.75rem" }}>
              Configure your APNs credentials from developer.apple.com
            </Alert>
            <TextField
              label="Service Key (.p8 contents)"
              value={iosFormData.serviceKey}
              onChange={handleIosFormChange("serviceKey")}
              fullWidth
              multiline
              rows={4}
              size="small"
            />
            <TextField
              label="Service Key ID"
              value={iosFormData.serviceKeyId}
              onChange={handleIosFormChange("serviceKeyId")}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIosModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleIosSave}
            variant="contained"
            disabled={
              !iosFormData.serviceKey ||
              !iosFormData.serviceKeyId ||
              isUpdatingIos
            }
          >
            {isUpdatingIos ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Android Setup Modal */}
      <Dialog
        open={androidModalOpen}
        onClose={() => setAndroidModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Android Push Notification Setup</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ py: 0.5, fontSize: "0.75rem" }}>
              Configure FCMv1 credentials from Firebase Console
            </Alert>
            <TextField
              label="Private Key"
              value={androidFormData.privateKey}
              onChange={handleAndroidFormChange("privateKey")}
              fullWidth
              multiline
              rows={4}
              size="small"
            />
            <TextField
              label="Project ID"
              value={androidFormData.projectId}
              onChange={handleAndroidFormChange("projectId")}
              fullWidth
              size="small"
            />
            <TextField
              label="Client Email"
              value={androidFormData.clientEmail}
              onChange={handleAndroidFormChange("clientEmail")}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAndroidModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAndroidSave}
            variant="contained"
            disabled={
              !androidFormData.privateKey ||
              !androidFormData.projectId ||
              !androidFormData.clientEmail ||
              isUpdatingAndroid
            }
          >
            {isUpdatingAndroid ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Test Push Modal */}
      <Dialog
        open={testPushModalOpen}
        onClose={() => setTestPushModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Test Push Notification</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ py: 0.5, fontSize: "0.75rem" }}>
              Send a test notification directly to a specific device token via
              Azure Notification Hub.
            </Alert>
            <TextField
              label="Device Token"
              value={testPushFormData.deviceToken}
              onChange={(e) =>
                setTestPushFormData((prev) => ({
                  ...prev,
                  deviceToken: e.target.value,
                }))
              }
              fullWidth
              size="small"
              placeholder="Paste device push token here"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Platform</InputLabel>
              <Select
                value={testPushFormData.platform}
                label="Platform"
                onChange={(e) =>
                  setTestPushFormData((prev) => ({
                    ...prev,
                    platform: e.target.value as "ios" | "android",
                  }))
                }
              >
                <MenuItem value="ios">iOS</MenuItem>
                <MenuItem value="android">Android</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Title"
              value={testPushFormData.title}
              onChange={(e) =>
                setTestPushFormData((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label="Body"
              value={testPushFormData.body}
              onChange={(e) =>
                setTestPushFormData((prev) => ({
                  ...prev,
                  body: e.target.value,
                }))
              }
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder={`This is a test push notification from ${brand.displayName}`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestPushModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendTestPush}
            variant="contained"
            disabled={
              !testPushFormData.deviceToken ||
              !testPushFormData.title ||
              isSendingTest
            }
          >
            {isSendingTest ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InternNotificationsCard;
